import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FEATURE_MODULES } from '@zag/features/common';
import { ZagIcon, ZagSwitch } from '@zag/ui';

export interface FeatureToggleChange {
  readonly feature: string;
  readonly enabled: boolean;
}

export interface FeatureBundleBulkToggle {
  readonly moduleKey: string;
  readonly features: readonly string[];
  readonly enabled: boolean;
}

/**
 * Visual grid of all catalogued features, grouped by their owning module.
 * Each row is a `<zag-switch>`; each bundle header carries an
 * "enable all" / "disable all" button pair so operators can flip an
 * entire module without touching individual features. Purely
 * presentational — the host owns the state and decides how mutations
 * are persisted (used by both the licence-definition editor and the
 * feature-flag editor).
 */
@Component({
  selector: 'zag-feature-toggle-grid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ZagIcon, ZagSwitch],
  template: `
    <div class="grid" role="group" [attr.aria-label]="ariaLabel()">
      @for (module of modules; track module.moduleKey) {
        <section class="module">
          <header class="module__header">
            <h3 class="module__title">{{ 'control.licensing.modules.' + module.moduleKey | translate }}</h3>
            <span class="module__count">{{
              'control.licensing.features.count' | translate: { selected: enabledCount(module.features), total: module.features.length }
            }}</span>
            @if (!readonly()) {
              <button
                type="button"
                class="module__bulk-btn"
                [attr.aria-label]="
                  (allEnabled(module.features) ? 'control.licensing.features.disableAll' : 'control.licensing.features.enableAll')
                    | translate
                "
                [title]="
                  (allEnabled(module.features) ? 'control.licensing.features.disableAll' : 'control.licensing.features.enableAll')
                    | translate
                "
                (click)="onBulkToggle(module.moduleKey, module.features, !allEnabled(module.features))"
              >
                <span [zagIcon]="allEnabled(module.features) ? 'remove_done' : 'done_all'" aria-hidden="true"></span>
              </button>
            }
          </header>
          <ul class="module__features">
            @for (feature of module.features; track feature) {
              <li class="feature" [class.feature--on]="enabledSet().has(feature)">
                <span class="feature__key">{{ feature }}</span>
                <zag-switch
                  [checked]="enabledSet().has(feature)"
                  [ariaLabel]="feature"
                  [disabled]="readonly()"
                  (checkedChange)="onToggle(feature, $event)"
                />
              </li>
            }
          </ul>
        </section>
      }
    </div>
  `,
  styleUrl: './feature-toggle-grid.component.scss',
})
export class FeatureToggleGridComponent {
  readonly enabledFeatures = input<readonly string[]>([]);
  readonly readonly = input<boolean>(false);
  readonly ariaLabel = input<string>('Features');

  readonly toggled = output<FeatureToggleChange>();
  readonly bulkToggled = output<FeatureBundleBulkToggle>();

  protected readonly modules = FEATURE_MODULES;

  protected readonly enabledSet = computed(() => new Set(this.enabledFeatures()));

  protected enabledCount(features: readonly string[]): number {
    const set = this.enabledSet();
    let n = 0;
    for (const f of features) if (set.has(f)) n++;
    return n;
  }

  protected allEnabled(features: readonly string[]): boolean {
    const set = this.enabledSet();
    return features.every(f => set.has(f));
  }

  protected onToggle(feature: string, enabled: boolean): void {
    if (this.readonly()) return;
    this.toggled.emit({ feature, enabled });
  }

  protected onBulkToggle(moduleKey: string, features: readonly string[], enabled: boolean): void {
    if (this.readonly()) return;
    this.bulkToggled.emit({ moduleKey, features, enabled });
  }
}
